/**
 * Sterne für erledigte Aufgaben (26.10.2026) auf dem Gerät. Wie das
 * Taschengeld gewöhnliche Sync-Zeilen (`star_entries`); Sterne für Aufgaben
 * bucht nur der Server, Erwachsene geben Extra-Sterne und lösen ein.
 */
import { newId } from '@kraftwerk/shared';
import { apiFetch } from '../api.js';
import { currentOwnerId } from '../auth.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalStarEntry } from './local.js';
import { stamp, writeRow } from './write.js';

/** Sterne eines Kindes — neueste zuerst. */
export async function starEntries(
  childId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalStarEntry[]> {
  return (await database.star_entries.where('childId').equals(childId).toArray())
    .filter((entry) => !entry.deletedAt)
    .sort((a, b) =>
      a.date === b.date ? b.updatedAt.localeCompare(a.updatedAt) : b.date.localeCompare(a.date),
    );
}

/** Extra-Sterne oder Einlösen — nur Erwachsene, das prüft der Server. */
export async function addStarEntry(
  input: {
    childId: string;
    householdId: string;
    kind: 'bonus' | 'redeem';
    stars: number;
    date: string;
    note: string | null;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalStarEntry> {
  const me = currentOwnerId();
  const row: LocalStarEntry = {
    id: newId(),
    ...(me ? { userId: me } : {}),
    ...input,
    updatedAt: stamp(),
    deletedAt: null,
  };
  await writeRow('star_entries', row, 'upsert', database);
  return row;
}

/** Eigene Buchung zurücknehmen — weich gelöscht, damit es jedes Gerät erfährt. */
export async function deleteStarEntry(
  entry: LocalStarEntry,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow('star_entries', { ...entry, updatedAt: now, deletedAt: now }, 'delete', database);
}

/** Sterne an/aus und die Belohnung — schreibt nur der Server, wie jede Mitgliedschaft. */
export async function setTaskStars(
  childId: string,
  input: { enabled: boolean; goalName: string | null; goalStars: number | null },
): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(childId)}/stars`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
